import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navigation } from '../App';
import { CheckCircle, Play, MessageSquare, BookOpen, Archive, Calendar, ArrowLeft } from 'lucide-react';

export default function MembershipPage() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6 group"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>

        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            Join the Luck or Skills Community
          </h1>
          <p className="text-xl text-muted-foreground">
            Learn Texas Hold'em from experienced players in a friendly, supportive environment
          </p>
        </div>

        {/* Cards Container */}
        <div className="max-w-6xl mx-auto">
          {/* Top Row: Two pricing cards side-by-side on desktop */}
          <div className="grid md:grid-cols-2 gap-8 items-stretch mb-8">
            {/* PREMIUM MEMBERSHIP CARD - LEFT on desktop, FIRST on mobile */}
            <div className="relative order-1">
              {/* Floating decoration elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-primary to-yellow-600 rounded-full blur-3xl opacity-30 animate-pulse"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-red-600 to-red-700 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              <div className="relative bg-gradient-to-br from-card via-card to-card/80 border-2 border-primary/40 rounded-3xl p-10 shadow-2xl hover:shadow-primary/30 transition-all overflow-hidden h-full">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 text-9xl opacity-5">♠</div>
                <div className="absolute bottom-0 left-0 text-9xl opacity-5 text-red-600">♥</div>
                
                {/* Premium badge */}
                <div className="relative mb-8 text-center">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/20 to-yellow-600/20 border border-primary/40 rounded-full px-6 py-2 mb-6">
                    <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
                    <span className="text-primary font-bold text-sm uppercase tracking-wider">Premium Membership</span>
                  </div>
                  
                  {/* Price Display */}
                  <div className="mb-2">
                    <span className="text-6xl sm:text-7xl font-heading font-bold text-primary glow-text" data-testid="membership-price-text">
                      $19.95
                    </span>
                  </div>
                  <div className="text-xl text-muted-foreground font-medium mb-6">
                    per month
                  </div>
                  
                  {/* CTA Button */}
                  <Link
                    to="/login"
                    className="inline-block w-full bg-gradient-to-r from-primary via-yellow-500 to-primary text-black py-5 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-primary/50 transition-all hover:-translate-y-1 shine mb-6"
                    data-testid="membership-cta-button"
                    style={{ backgroundSize: '200% 100%', backgroundPosition: '0% 0%' }}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span className="text-2xl">🏆</span>
                      BECOME A MEMBER
                      <span className="text-2xl">🏆</span>
                    </span>
                  </Link>
                  
                  <p className="text-sm text-muted-foreground">
                    Cancel anytime • No hidden fees • Instant access
                  </p>
                </div>

                {/* Features Grid */}
                <div className="space-y-3 relative z-10">
                  <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Play className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Live Poker Streams</div>
                      <div className="text-sm text-muted-foreground">Weekly live discussions with expert players</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Strategy Lessons</div>
                      <div className="text-sm text-muted-foreground">Master Texas Hold'em from basics to advanced</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Community Forum</div>
                      <div className="text-sm text-muted-foreground">Get feedback, share hands, connect with winners</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center flex-shrink-0">
                      <Archive className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Stream Archives</div>
                      <div className="text-sm text-muted-foreground">Watch past sessions anytime, anywhere</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Hand Reviews</div>
                      <div className="text-sm text-muted-foreground">Submit your hands for expert analysis</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Exclusive Events</div>
                      <div className="text-sm text-muted-foreground">Access to special poker workshops and Q&As</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FREE TRIAL CARD - RIGHT on desktop, SECOND on mobile */}
            <div className="relative order-2">
              {/* Glow effects */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full blur-3xl opacity-30 animate-pulse"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              <div className="relative bg-gradient-to-br from-emerald-900/20 via-card to-card/80 border-2 border-emerald-500/40 rounded-3xl p-10 shadow-2xl hover:shadow-emerald-500/20 transition-all overflow-hidden h-full">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 text-9xl opacity-5 text-emerald-500">♣</div>
                <div className="absolute bottom-0 left-0 text-9xl opacity-5 text-emerald-600">♦</div>
                
                {/* Free Trial Badge */}
                <div className="relative mb-8 text-center">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-blue-600/20 border border-emerald-500/40 rounded-full px-6 py-2 mb-6">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                    <span className="text-emerald-400 font-bold text-sm uppercase tracking-wider">Try It Free</span>
                  </div>
                  
                  {/* Free Trial Display */}
                  <div className="mb-2">
                    <span className="text-6xl sm:text-7xl font-heading font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent">
                      FREE
                    </span>
                  </div>
                  <div className="text-xl text-muted-foreground font-medium mb-6">
                    3-day trial • No credit card required
                  </div>
                  
                  {/* CTA Button */}
                  <Link
                    to="/login?trial=true"
                    className="inline-block w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-4 px-8 rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-emerald-500/50 mb-6"
                    data-testid="start-free-trial-btn"
                  >
                    Start Free Trial →
                  </Link>
                  
                  <p className="text-sm text-emerald-400/80 italic">
                    Get full access to the platform risk-free
                  </p>
                </div>
                
                {/* Trial Benefits */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-foreground">Full access during trial</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-foreground">Learn & play poker</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-foreground">Access all features</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-foreground">No commitment</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-foreground">Cancel anytime</span>
                  </div>
                </div>
                
                {/* Trial Notice */}
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-emerald-400">
                    Perfect for beginners who want to explore before committing
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row: "What You'll Get" centered below - THIRD on mobile */}
          <div className="max-w-3xl mx-auto order-3">
            <div className="space-y-6">
              <h2 className="text-2xl font-heading font-bold mb-6">What You'll Get</h2>
              
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1">Live Poker Discussions</h3>
                  <p className="text-sm text-muted-foreground">
                    Join weekly livestreams where our panel discusses strategy, reviews hands,
                    and answers your questions in real-time.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1">Community Forum</h3>
                  <p className="text-sm text-muted-foreground">
                    Ask questions, share experiences, and get feedback from the community
                    and our expert panel members.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1">Strategy Lessons</h3>
                  <p className="text-sm text-muted-foreground">
                    Comprehensive lessons covering everything from basic rules to advanced
                    concepts, all explained clearly for beginners.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Archive className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold mb-1">Archived Streams</h3>
                  <p className="text-sm text-muted-foreground">
                    Can't make the live stream? Watch past discussions anytime at your convenience.
                  </p>
                </div>
              </div>

              <div className="bg-secondary/50 border border-border rounded-lg p-6 mt-8">
                <h3 className="font-heading font-semibold mb-3">Why We're Different</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>✓ Beginner-focused (no question is too basic)</li>
                  <li>✓ Educational, not gambling-focused</li>
                  <li>✓ Respectful, welcoming community</li>
                  <li>✓ Cancel anytime, no commitments</li>
                </ul>
              </div>

              {/* Footer */}
              <div className="mt-12 pt-6 border-t border-border text-center">
                <p className="text-muted-foreground text-sm mb-2">© 2024 Luck or Skills</p>
                <p className="text-xs text-muted-foreground/40 italic mb-3">"Kiss a Pig" - Patrick Lynch</p>
                <Link 
                  to="/admin" 
                  className="text-xs text-foreground/30 hover:text-[#C9A227] transition-colors"
                  data-testid="footer-admin-link"
                >
                  Admin Access
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
